import React, { useMemo, useState } from "react";

import { PositionsOrVirtualPortfolioPlanStats } from "@fartherfinance/frontend/api/Investment/request/postPositionsOrVirtualPortfolioPlanStats";
import { TransitionProposalPerformanceMetric } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { getRiskProfileFromFinScore } from "@fartherfinance/frontend/utils/transitionProposals/getRiskProfileFromFinScore";

import NoDataSet from "@src/multiCustodian/components/Portfolios/NoDataSet";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import TextWithPerformance from "@src/sharedComponents/TextWithPerformance/TextWithPerformance";
import { assertUnreachable } from "@src/utils/assertUnreachable";
import roundToN from "@src/utils/roundToN";
import toPercent from "@src/utils/toPercent";

import { strings } from "./strings";
import TableRow from "./TabelRow";

const NO_DATA = "- -";

type OneThreeFiveTenYearString = "1Y" | "3Y" | "5Y" | "10Y";
type ThreeFiveTenYearString = "3Y" | "5Y" | "10Y";
type FiveTenYearString = "5Y" | "10Y";

interface OneThreeFiveTenYearOption {
  label: OneThreeFiveTenYearString;
  id: OneThreeFiveTenYearString;
}

interface ThreeFiveTenYearOption {
  label: ThreeFiveTenYearString;
  id: ThreeFiveTenYearString;
}

interface FiveTenYearOption {
  label: FiveTenYearString;
  id: FiveTenYearString;
}

const oneThreeFiveTenYearOptions: OneThreeFiveTenYearOption[] = [
  {
    id: "1Y",
    label: "1Y",
  },
  {
    id: "3Y",
    label: "3Y",
  },
  {
    id: "5Y",
    label: "5Y",
  },
  {
    id: "10Y",
    label: "10Y",
  },
] as const;

const threeFiveTenYearOptions: ThreeFiveTenYearOption[] = [
  {
    id: "3Y",
    label: "3Y",
  },
  {
    id: "5Y",
    label: "5Y",
  },
  {
    id: "10Y",
    label: "10Y",
  },
] as const;

const fiveTenYearOptions: FiveTenYearOption[] = [
  {
    id: "5Y",
    label: "5Y",
  },
  {
    id: "10Y",
    label: "10Y",
  },
] as const;

const historicalReturnKeyMap: Record<
  string,
  TransitionProposalPerformanceMetric
> = {
  "1Y": "returnTotalHist1yr",
  "5Y": "returnTotalHist5yr",
  "10Y": "returnTotalHist10yr",
};

const volatilityKeyMap: Record<string, TransitionProposalPerformanceMetric> = {
  "1Y": "volatilityHistMonthly1yr",
  "5Y": "volatilityHistMonthly5yr",
  "10Y": "volatilityHistMonthly10yr",
};

interface Props {
  isLoading: boolean;
  hasError: boolean;
  data: PositionsOrVirtualPortfolioPlanStats | undefined;
  metricVisibilitySettings?: Record<
    TransitionProposalPerformanceMetric,
    boolean
  >;
  onToggleMetricVisibility?: (
    key: TransitionProposalPerformanceMetric
  ) => () => void;
  disableMetricVisibilityToggle?: boolean;
  hideMetricVisibilityTogglesAndShowAllStats?: boolean;
}

const PortfolioMetricsTable: React.FC<Props> = ({
  isLoading,
  hasError,
  data,
  metricVisibilitySettings = {} as Record<
    TransitionProposalPerformanceMetric,
    boolean
  >,
  onToggleMetricVisibility = () => () => {},
  disableMetricVisibilityToggle = false,
  hideMetricVisibilityTogglesAndShowAllStats = false,
}) => {
  const applicationMode = useApplicationMode();
  const isAdvisor =
    applicationMode.isImpersonationMode || applicationMode.isAdvisorMode;

  const [historicalReturnOption, setHistoricalReturnOption] = useState<
    DropdownMenuOption<OneThreeFiveTenYearString>
  >(oneThreeFiveTenYearOptions[0]);

  const [volatilityOption, setVolatilityOption] = useState<
    DropdownMenuOption<OneThreeFiveTenYearString>
  >(oneThreeFiveTenYearOptions[0]);

  const [marketCaptureOption, setMarketCaptureOption] = useState<
    DropdownMenuOption<FiveTenYearString>
  >(fiveTenYearOptions[0]);

  const [maxDrawdownOption, setMaxDrawdownOption] = useState<
    DropdownMenuOption<ThreeFiveTenYearString>
  >(threeFiveTenYearOptions[0]);

  /* Historical Return Vars */
  const historicalReturn = useMemo(() => {
    switch (historicalReturnOption.id) {
      case "1Y":
        return data?.data.returnTotalHist1yr ?? undefined;
      case "3Y":
        return data?.data.returnTotalHist3yr ?? undefined;
      case "5Y":
        return data?.data.returnTotalHist5yr ?? undefined;
      case "10Y":
        return data?.data.returnTotalHist10yr ?? undefined;
      default:
        return assertUnreachable(historicalReturnOption.id);
    }
  }, [data, historicalReturnOption.id]);

  const historicalReturnVisibility = useMemo(
    () =>
      ![
        "returnTotalHist1yr",
        "returnTotalHist3yr",
        "returnTotalHist5yr",
        "returnTotalHist10yr",
      ].some(
        (v) =>
          metricVisibilitySettings[v as TransitionProposalPerformanceMetric] ===
          false
      ),
    [metricVisibilitySettings]
  );

  const historicalReturnKey = useMemo(
    () =>
      historicalReturnKeyMap[
        historicalReturnOption.id as keyof typeof historicalReturnKeyMap
      ],
    [historicalReturnOption.id]
  );

  /* Trailing Yield Vars */
  const trailingYieldVisiblity = useMemo(
    () =>
      metricVisibilitySettings[
        "yield1yr" as TransitionProposalPerformanceMetric
      ] !== false,
    [metricVisibilitySettings]
  );

  /* FinScore Vars */
  const finScoreVisibility = useMemo(
    () =>
      metricVisibilitySettings[
        "finscore" as TransitionProposalPerformanceMetric
      ] !== false,
    [metricVisibilitySettings]
  );

  /* Volatility Vars */
  const volatility = useMemo(() => {
    switch (volatilityOption.id) {
      case "1Y":
        return data?.data.volatilityHistMonthly1yr
          ? toPercent(parseFloat(data.data.volatilityHistMonthly1yr))
          : NO_DATA;
      case "3Y":
        return data?.data.volatilityHistMonthly3yr
          ? toPercent(parseFloat(data.data.volatilityHistMonthly3yr))
          : NO_DATA;
      case "5Y":
        return data?.data.volatilityHistMonthly5yr
          ? toPercent(parseFloat(data.data.volatilityHistMonthly5yr))
          : NO_DATA;
      case "10Y":
        return data?.data.volatilityHistMonthly10yr
          ? toPercent(parseFloat(data.data.volatilityHistMonthly10yr))
          : NO_DATA;
      default:
        return assertUnreachable(volatilityOption.id);
    }
  }, [data, volatilityOption.id]);

  const volatilityVisibility = useMemo(
    () =>
      ![
        "volatilityHistMonthly1yr",
        "volatilityHistMonthly3yr",
        "volatilityHistMonthly5yr",
        "volatilityHistMonthly10yr",
      ].some(
        (v) =>
          metricVisibilitySettings[v as TransitionProposalPerformanceMetric] ===
          false
      ),
    [metricVisibilitySettings]
  );

  const volatilityKey = useMemo(
    () =>
      volatilityKeyMap[volatilityOption.id as keyof typeof volatilityKeyMap],
    [volatilityOption.id]
  );

  /* Market Capture Vars */
  const marketCapture = useMemo(() => {
    switch (marketCaptureOption.id) {
      case "5Y":
        return `${
          data?.data.upMarketCaptureHybFb5yr
            ? toPercent(parseFloat(data.data.upMarketCaptureHybFb5yr))
            : " - "
        } / ${
          data?.data.downMarketCaptureHybFb5yr
            ? toPercent(parseFloat(data.data.downMarketCaptureHybFb5yr))
            : " - "
        }`;
      case "10Y":
        return `${
          data?.data.upMarketCaptureHybFb10yr
            ? toPercent(parseFloat(data.data.upMarketCaptureHybFb10yr))
            : " - "
        } / ${
          data?.data.downMarketCaptureHybFb10yr
            ? toPercent(parseFloat(data.data.downMarketCaptureHybFb10yr))
            : " - "
        }`;
      default:
        return assertUnreachable(marketCaptureOption.id);
    }
  }, [data, marketCaptureOption.id]);

  /* Max Drawdown Vars */
  const maxDrawdown = useMemo(() => {
    switch (maxDrawdownOption.id) {
      case "3Y":
        return data?.data.drawdownMaxHistMonthly3yr
          ? toPercent(parseFloat(data.data.drawdownMaxHistMonthly3yr))
          : NO_DATA;
      case "5Y":
        return data?.data.drawdownMaxHistMonthly5yr
          ? toPercent(parseFloat(data.data.drawdownMaxHistMonthly5yr))
          : NO_DATA;
      case "10Y":
        return data?.data.drawdownMaxHistMonthly10yr
          ? toPercent(parseFloat(data.data.drawdownMaxHistMonthly10yr))
          : NO_DATA;
      default:
        return assertUnreachable(maxDrawdownOption.id);
    }
  }, [data, maxDrawdownOption.id]);

  if (hasError === true) {
    return <NoDataSet text={"Error loading stats"} iconColor={"error"} />;
  }

  if (isLoading === false && data === undefined) {
    return <NoDataSet text={"Data set empty"} iconColor={"info"} />;
  }

  return (
    <React.Fragment>
      {/* Historical Return */}
      <TableRow<OneThreeFiveTenYearString>
        label="Historical Return"
        controlType="dropdown"
        dropdownOptions={oneThreeFiveTenYearOptions}
        selectedDropdownOption={historicalReturnOption}
        onDropdownSelect={(el) => setHistoricalReturnOption(el)}
        customValueNode={
          <TextWithPerformance numberText={historicalReturn} isPercent={true} />
        }
        expandableContent={strings["Historical Return"]}
        isLoading={isLoading}
        isAdvisor={isAdvisor}
        onToggleVisibility={
          hideMetricVisibilityTogglesAndShowAllStats
            ? undefined
            : onToggleMetricVisibility(historicalReturnKey)
        }
        isVisible={historicalReturnVisibility}
        disableVisibilityToggle={disableMetricVisibilityToggle}
      />

      {/* Trailing Yield */}
      <TableRow
        label="Trailing Yield"
        controlType="fixedText"
        fixedText="12M"
        value={
          data?.data.yield1yr
            ? `${toPercent(parseFloat(data.data.yield1yr))}`
            : NO_DATA
        }
        expandableContent={strings["Trailing Yield"]}
        isLoading={isLoading}
        isAdvisor={isAdvisor}
        onToggleVisibility={
          hideMetricVisibilityTogglesAndShowAllStats
            ? undefined
            : onToggleMetricVisibility("yield1yr")
        }
        isVisible={trailingYieldVisiblity}
        disableVisibilityToggle={disableMetricVisibilityToggle}
      />

      {/* Risk Profile */}
      <TableRow
        label="Risk Profile"
        value={getRiskProfileFromFinScore(data?.data.finscore ?? undefined)}
        expandableContent={strings["Risk Profile"]}
        isLoading={isLoading}
        isAdvisor={isAdvisor}
        onToggleVisibility={
          hideMetricVisibilityTogglesAndShowAllStats
            ? undefined
            : onToggleMetricVisibility("finscore")
        }
        isVisible={finScoreVisibility}
        disableVisibilityToggle={disableMetricVisibilityToggle}
      />

      {/* Volatility  */}
      <TableRow<OneThreeFiveTenYearString>
        label="Volatility"
        controlType="dropdown"
        dropdownOptions={oneThreeFiveTenYearOptions}
        selectedDropdownOption={volatilityOption}
        onDropdownSelect={(el) => setVolatilityOption(el)}
        value={volatility}
        expandableContent={strings["Volatility"]}
        isLoading={isLoading}
        isAdvisor={isAdvisor}
        onToggleVisibility={
          hideMetricVisibilityTogglesAndShowAllStats
            ? undefined
            : onToggleMetricVisibility(volatilityKey)
        }
        isVisible={volatilityVisibility}
        disableVisibilityToggle={disableMetricVisibilityToggle}
      />

      {hideMetricVisibilityTogglesAndShowAllStats && (
        <>
          {/* Up/Down Market Capture */}
          <TableRow<FiveTenYearString>
            label="Up/Down Market Capture"
            controlType="dropdown"
            dropdownOptions={fiveTenYearOptions}
            selectedDropdownOption={marketCaptureOption}
            onDropdownSelect={(el) => setMarketCaptureOption(el)}
            value={marketCapture}
            expandableContent={strings["Up/Down Market Capture"]}
            isLoading={isLoading}
            isAdvisor={isAdvisor}
            onToggleVisibility={undefined}
            isVisible={true}
            disableVisibilityToggle={disableMetricVisibilityToggle}
          />

          {/* Global Equity Beta */}
          <TableRow
            label="Global Equity Beta"
            value={
              data?.data.betaEquityUsSinglefactor
                ? `${roundToN(
                    parseFloat(data.data.betaEquityUsSinglefactor),
                    3
                  )}`
                : NO_DATA
            }
            expandableContent={strings["Global Equity Beta"]}
            isLoading={isLoading}
            isAdvisor={isAdvisor}
            onToggleVisibility={undefined}
            isVisible={true}
            disableVisibilityToggle={disableMetricVisibilityToggle}
          />

          {/* Max Drawdown */}
          <TableRow<ThreeFiveTenYearString>
            label="Max Drawdown"
            controlType="dropdown"
            dropdownOptions={threeFiveTenYearOptions}
            selectedDropdownOption={maxDrawdownOption}
            onDropdownSelect={(el) => setMaxDrawdownOption(el)}
            value={maxDrawdown}
            hideBottomBorder={true}
            expandableContent={strings["Max Drawdown"]}
            isLoading={isLoading}
            isAdvisor={isAdvisor}
            onToggleVisibility={undefined}
            isVisible={true}
            disableVisibilityToggle={disableMetricVisibilityToggle}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default PortfolioMetricsTable;
